import { SingleSelectDropdown, TextInput } from "@components/library";
import { DropdownOption } from "@components/library/Dropdowns/DropdownOption";
import KeywordsDropdown from "@components/library/Dropdowns/KeywordsDropdown/KeywordsDropdown";
import { COUNTRY_OPTIONS, FEATURE_FLAGS, FONTS } from "@constants";
import { getOptionFromValue, getValueFromOption } from "@utils/dropdownUtils";
import { isValidURL } from "@utils/textUtils";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useCompanyOptions } from "../Hooks";
import { BaseOnboardingProfileInfo } from "../OnboardingContainer";
import * as S from "../StepStyles";

export type CompanyScientistOnboardingProfileInfo = BaseOnboardingProfileInfo &
  Partial<{
    selectedCompanyId?: number;
    companyName?: string;
    companyURL: string;
    country_code: string | undefined;
    role: string;
    disciplines: string[];
    areasOfExpertise: string[];
  }>;

interface Props {
  profileInfo: CompanyScientistOnboardingProfileInfo;
  setProfileInfo: Dispatch<SetStateAction<CompanyScientistOnboardingProfileInfo>>;
  prefillCompanyName?: string;
}

export const STARTUP_TITLES = ["Business Development", "CEO", "CSO", "Researcher"];
export const STARTUP_TITLE_OPTIONS = STARTUP_TITLES.map(getOptionFromValue);
export const companyRoleOptions = STARTUP_TITLES.map((text) => ({
  label: text,
  value: text,
}));

function CompanyScientist({ profileInfo, setProfileInfo, prefillCompanyName = "" }: Props) {
  const [companyOptions] = useCompanyOptions();

  const {
    role,
    disciplines,
    areasOfExpertise,
    country_code,
    companyName,
    companyURL,
    selectedCompanyId,
  } = profileInfo;

  console.log("profileInfo", profileInfo);

  const handleCompanyDropdown = (e, selection) => {
    if (selection?.action === "select-option" || selection?.action === "create-option") {
      setProfileInfo((oldProfileInfo) => ({
        ...oldProfileInfo,
        ...(e.__isNew__
          ? { selectedCompanyId: undefined, companyName: e.label }
          : { selectedCompanyId: e.value, companyName: e.label }),
      }));
    } else if (selection?.action === "clear") {
      setProfileInfo((oldProfileInfo) => ({
        ...oldProfileInfo,
        selectedCompanyId: undefined,
        companyName: undefined,
      }));
    }
  };

  const handleCompanyInput = (e: ChangeEvent<HTMLInputElement>) => {
    setProfileInfo((oldProfileInfo) => ({
      ...oldProfileInfo,
      companyName: e.target.value,
    }));
  };

  const handleCompanyWebsite = (e) => {
    setProfileInfo((oldProfileInfo) => ({
      ...oldProfileInfo,
      companyURL: e.target.value,
    }));
  };

  const handleCountryChange = (option) =>
    setProfileInfo((oldProfileInfo) => ({
      ...oldProfileInfo,
      country_code: getValueFromOption(option),
    }));

  let selectedCompanyOption: DropdownOption | null = null;
  if (selectedCompanyId) {
    selectedCompanyOption =
      companyOptions.find((option) => option.value === String(profileInfo.selectedCompanyId)) ??
      null;
  } else if (companyName) {
    selectedCompanyOption = getOptionFromValue(companyName);
  }

  const isNewCompany = Boolean(!selectedCompanyId && companyName);

  return (
    <>
      {FEATURE_FLAGS.REPLACE_STARTUP_WITH_COMPANY ? (
        <>
          <S.DropdownRow>
            <SingleSelectDropdown
              label="Company namee"
              labelBottomMargin="0"
              labelFont={FONTS.SEMIBOLD_1}
              withSearchIcon
              placeholder="Search company names or add new"
              isCreatable
              isClearable
              options={companyOptions}
              onChange={handleCompanyDropdown}
              value={selectedCompanyOption}
              defaultInputValue={prefillCompanyName}
              data-testid="company-scientist-company-name-input"
            />
          </S.DropdownRow>
          {isNewCompany && (
            <>
              <S.DropdownRow>
                <TextInput
                  label="Company website"
                  labelFont={FONTS.SEMIBOLD_1}
                  value={companyURL ?? ""}
                  onChange={(e) => handleCompanyWebsite(e)}
                  placeholder="Enter company website URL"
                  hideClearButton
                  data-testid="company-scientist-company-website-input"
                />
              </S.DropdownRow>
              <S.DropdownRow>
                <SingleSelectDropdown
                  label="Company location"
                  labelBottomMargin="0"
                  labelFont={FONTS.SEMIBOLD_1}
                  withSearchIcon
                  placeholder="Search countries"
                  value={getOptionFromValue(country_code)}
                  options={COUNTRY_OPTIONS}
                  onChange={handleCountryChange}
                  data-testid="company-scientist-country-dropdown"
                />
              </S.DropdownRow>
            </>
          )}
        </>
      ) : (
        <>
          <S.DropdownRow>
            <S.DropdownTitle>Company name</S.DropdownTitle>
            <TextInput
              placeholder="Enter company name"
              onChange={handleCompanyInput}
              value={profileInfo?.companyName ?? prefillCompanyName}
              data-testid="company-scientist-company-name-input"
            />
          </S.DropdownRow>
          <S.DropdownRow>
            <S.DropdownTitle>Company location</S.DropdownTitle>
            <SingleSelectDropdown
              withSearchIcon
              placeholder="Search countries"
              value={getOptionFromValue(country_code)}
              options={COUNTRY_OPTIONS}
              onChange={handleCountryChange}
              data-testid="company-scientist-country-dropdown"
            />
          </S.DropdownRow>
          <S.DropdownRow>
            <S.DropdownTitle>Company website</S.DropdownTitle>
            <TextInput
              placeholder="Enter company website URL"
              onChange={handleCompanyWebsite}
              value={profileInfo?.companyURL ?? ""}
              data-testid="company-scientist-company-website-input"
              errors={[
                {
                  hasError:
                    Boolean(profileInfo?.companyURL?.length) &&
                    !isValidURL(profileInfo?.companyURL),
                  errorMessage: "Please enter a valid URL",
                },
              ]}
            />
          </S.DropdownRow>
        </>
      )}
      <S.DropdownRow>
        <S.DropdownTitle>What is your job title?</S.DropdownTitle>
        <TextInput
          placeholder="Enter your job title"
          onChange={(e) =>
            setProfileInfo((oldProfileInfo) => ({
              ...oldProfileInfo,
              role: e.target.value,
            }))
          }
          value={role ?? ""}
          data-testid="company-scientist-role-input"
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <S.DropdownTitle>Which disciplines are most relevant to your work?</S.DropdownTitle>
        <KeywordsDropdown
          keywordType="discipline"
          targetType="scientist"
          value={disciplines ?? []}
          onChange={(selection) => setProfileInfo({ ...profileInfo, disciplines: selection })}
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <S.DropdownTitle>What is your expertise?</S.DropdownTitle>
        <KeywordsDropdown
          keywordType="area_of_expertise"
          targetType="scientist"
          value={areasOfExpertise ?? []}
          onChange={(selection) => setProfileInfo({ ...profileInfo, areasOfExpertise: selection })}
        />
      </S.DropdownRow>
    </>
  );
}

export default CompanyScientist;
